import { CalendarClock, Clock, Phone, Star, BadgeCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CATEGORY_MAP, fmtDate, imgSrc } from "@/lib/categories";
import { DirectionsMenu } from "@/components/DirectionsMenu";
import { track } from "@/lib/track";
import { openStatus, hasShowTonight } from "@/lib/hours";
import { RemindBell } from "@/components/RemindBell";
import { HeartButton } from "@/components/HeartButton";

export function ReservationBadge({ item }) {
  return (
    <span data-testid={`reservation-badge-${item.id}`}
      className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${item.reservation_required ? "bg-[#EF4444]/15 text-[#FF7A7A]" : "bg-[#10B981]/15 text-[#34D399]"}`}>
      {item.reservation_required ? "Reservation needed" : "Walk-ins welcome"}
    </span>
  );
}

export function VerifiedBadge({ item, className = "" }) {
  if (item.source !== "google") return null;
  return (
    <span data-testid={`verified-badge-${item.id}`} title="Venue details confirmed by Google"
      className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide bg-[#0B0F19]/85 backdrop-blur text-[#4FC3F7] border border-[#4FC3F7]/40 px-2.5 py-1 rounded-full ${className}`}>
      <ShieldCheck className="h-3.5 w-3.5" /> Verified by Google
    </span>
  );
}

function LiveBadges({ item }) {
  const open = openStatus(item.hours);
  return (
    <>
      {hasShowTonight(item) && <span data-testid={`tonight-badge-${item.id}`} className="text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#8A2BE2]/20 text-[#C084FC]">Show tonight</span>}
      <RemindBell item={item} show={(item.schedule || []).find((s) => s.date >= new Date().toLocaleDateString("en-CA"))} tid={`remind-card-${item.id}`} compact />
      {open !== null && <span data-testid={`open-badge-${item.id}`} className={`text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full ${open ? "bg-[#00E5A3]/15 text-[#00E5A3]" : "bg-slate-500/15 text-slate-400"}`}>{open ? "Open now" : "Closed now"}</span>}
    </>
  );
}

function CardMedia({ item, cat }) {
  const Icon = cat.icon;
  return (
    <div className="relative h-40 overflow-hidden">
      <img src={imgSrc(item.image_url) || cat.img} alt={item.name} loading="lazy" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#151C2C] via-[#151C2C]/20 to-transparent" />
      <span className="absolute top-3 left-3 flex items-center gap-1.5 text-xs font-semibold bg-[#0B0F19]/80 backdrop-blur px-2.5 py-1 rounded-full">
        <Icon className="h-3.5 w-3.5 text-[#FF3B6B]" /> {cat.label}
      </span>
      {item.featured && (
        <span className="absolute top-3 right-3 flex items-center gap-1 text-xs font-bold bg-[#FFB800] text-[#0B0F19] px-2.5 py-1 rounded-full">
          <BadgeCheck className="h-3.5 w-3.5" /> Featured
        </span>
      )}
      {item.is_new && <span data-testid={`new-badge-${item.id}`} className="absolute top-12 right-3 text-[11px] font-bold bg-[#00E5A3] text-[#0B0F19] px-2 py-0.5 rounded-full">New</span>}
      <VerifiedBadge item={item} className="absolute bottom-3 right-3" />
    </div>
  );
}

function CardRating({ item }) {
  if (item.review_count > 0) {
    return <span data-testid={`community-rating-${item.id}`} className="flex items-center gap-1 text-sm text-[#FFB800] shrink-0" title="Traveler reviews"><Star className="h-3.5 w-3.5 fill-current" />{item.review_avg}<span className="text-slate-500 text-xs">({item.review_count})</span></span>;
  }
  if (!item.rating) return null;
  return <span className="flex items-center gap-1 text-sm text-[#FFB800] shrink-0"><Star className="h-3.5 w-3.5 fill-current" />{Number(item.rating).toFixed(1)}</span>;
}

function CardBadges({ item }) {
  return (
    <div className="flex items-center gap-2 flex-wrap mt-auto pt-1">
      <ReservationBadge item={item} />
      <LiveBadges item={item} />
      {item.deals?.length > 0 && <span data-testid={`deal-badge-${item.id}`} className="text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full bg-[#FF3B6B] text-white">Deal</span>}
      {item.price_range && <span className="text-xs text-slate-400 font-mono-vv">{item.price_range}</span>}
      {item.distance != null && <span data-testid={`distance-${item.id}`} className="text-xs text-[#00E5A3] font-mono-vv ml-auto">{item.distance < 0.1 ? "here" : `${item.distance.toFixed(1)} mi`}</span>}
    </div>
  );
}

function CardActions({ item }) {
  return (
    <div className="flex gap-2 pt-1">
      <DirectionsMenu item={item} />
      {item.phone && (
        <Button asChild variant="outline" className="rounded-full h-9 border-[#26334D] bg-transparent hover:bg-[#1E293B]">
          <a data-testid={`phone-call-button-${item.id}`} href={`tel:${item.phone.replace(/[^\d+]/g, "")}`} onClick={(e) => { e.stopPropagation(); track(item, "call"); }}>
            <Phone className="h-4 w-4 mr-1.5" /> Call
          </a>
        </Button>
      )}
      <HeartButton item={item} className="ml-auto" />
    </div>
  );
}

export function ListingCard({ item, index, onOpen }) {
  const cat = CATEGORY_MAP[item.category] || CATEGORY_MAP.shows;
  const next = (item.schedule || [])[0];

  return (
    <article data-testid={`listing-card-${item.id}`} onClick={() => onOpen(item)}
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
      className={`rise card-lift cursor-pointer rounded-2xl overflow-hidden bg-[#151C2C] border flex flex-col ${item.featured ? "border-[#FFB800]/60" : "border-[#26334D]"}`}>
      <CardMedia item={item} cat={cat} />
      <div className="p-4 flex flex-col gap-3 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-lg font-semibold leading-snug">{item.name}</h3>
          <CardRating item={item} />
        </div>
        <p className="text-sm text-slate-400 line-clamp-2">{item.description}</p>
        <div className="text-sm text-slate-300 flex items-start gap-2">
          {next ? <CalendarClock className="h-4 w-4 mt-0.5 text-[#00E5A3] shrink-0" /> : <Clock className="h-4 w-4 mt-0.5 text-[#00E5A3] shrink-0" />}
          <span className="line-clamp-2">{next ? `Next: ${fmtDate(next.date)} · ${next.time}` : item.hours || "Hours vary — call ahead"}</span>
        </div>
        <CardBadges item={item} />
        <CardActions item={item} />
      </div>
    </article>
  );
}
